import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Menu, X, Sun, Moon } from 'lucide-react'
import { useAuth, useClientConfig, useTheme } from '@workspace/public-utils'

const navItems = [
  { label: '당첨결과', path: '/results' },
  { label: '번호분석', path: '/analysis' },
  { label: '구독플랜', path: '/plans' },
  { label: '고객지원', path: '/faq' },
]

const StarHeader = () => {
  const { user, logout } = useAuth()
  const config = useClientConfig()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm border-b border-gray-100 dark:border-gray-800'
          : 'bg-white dark:bg-gray-900 border-b border-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: config.logoIconColor }}
            >
              <span className="text-white font-black text-sm">{config.logoIconText}</span>
            </div>
            <span className="text-lg font-extrabold tracking-tight text-star-navy dark:text-white">
              {config.brandName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive(item.path)
                    ? 'text-star-navy dark:text-star-gold bg-gray-50 dark:bg-gray-800'
                    : 'text-gray-500 dark:text-gray-400 hover:text-star-navy dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth + Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            >
              {theme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
            </button>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-star-navy dark:hover:text-white transition-colors"
                >
                  마이페이지
                </Link>
                <button
                  onClick={logout}
                  className="text-sm font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  로그아웃
                </button>
              </>
            ) : (
              <Link
                to="/auth/login"
                className="star-btn-primary !py-2 !px-5 !text-sm"
              >
                로그인
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-1 md:hidden">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            >
              {theme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
            </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 -mr-2 text-gray-500 dark:text-gray-400 hover:text-star-navy dark:hover:text-white transition-colors"
            aria-label="메뉴 열기"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 dark:border-gray-800 py-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-star-navy dark:text-star-gold bg-gray-50 dark:bg-gray-800'
                    : 'text-gray-500 dark:text-gray-400 hover:text-star-navy dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800">
              {user ? (
                <div className="space-y-1">
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-star-navy dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    마이페이지
                  </Link>
                  <button
                    onClick={logout}
                    className="block w-full text-left px-3 py-2.5 text-sm font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    로그아웃
                  </button>
                </div>
              ) : (
                <Link to="/auth/login" className="star-btn-primary w-full !text-sm">
                  로그인
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default StarHeader
